/**
 * Mojo Up AI Cloud: the hosted edition for Free and Teams (docs/plans/2026-10-mojo-up-cloud.md).
 *
 * Cloud issues its own tokens. Google, GitHub, Microsoft, Facebook and an emailed code only prove
 * who someone is; people, organisations, memberships and roles live in Cloud. A person's session
 * is not tied to an organisation: each request names one with the `X-Aioe-Organisation` header.
 */
import { z } from "zod";
export const CLOUD_ORGANISATION_HEADER = "x-aioe-organisation";
export const editionSchema = z.enum(["enterprise", "cloud"]);
export const signInProviderSchema = z.enum(["google", "github", "microsoft", "facebook", "email"]);
/** Which clients may sign in, and so which redirect addresses are allowed. */
export const signInClientSchema = z.enum(["console", "mobile"]);
export const cloudRoleSchema = z.enum(["owner", "admin", "member", "viewer"]);
/** personal: one person's Free home, created at first sign-in; team: shared, takes members. */
export const organisationKindSchema = z.enum(["personal", "team"]);
export const cloudPlanSchema = z.enum(["free", "teams"]);
/** `GET /auth/v1/providers`: the sign-in buttons to show. */
export const signInProvidersSchema = z.object({
    providers: z.array(z.object({ id: signInProviderSchema, label: z.string() })),
});
/** `POST /auth/v1/email/start`. Always answered 202, so it never says whether an address is known. */
export const emailStartSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
    client: signInClientSchema,
    redirect_uri: z.string().min(1).max(500),
    code_challenge: z.string().min(43).max(128),
    code_challenge_method: z.literal("S256").default("S256"),
    state: z.string().max(500).optional(),
    /** A ticket from `POST /cloud/v1/me/sign-in-methods/link`: the code adds this address to the signed-in person. */
    link: z.string().max(300).optional(),
    /**
     * The `confirm` ticket a provider's round trip came back with (its address was not one the provider verified):
     * the code proves the address, and the provider's account then joins the person who holds it, or a new one.
     */
    confirm: z.string().max(300).optional(),
    /** Cloudflare Turnstile's answer, when discovery names a `turnstileSiteKey` (else ignored). */
    turnstileToken: z.string().max(2048).optional(),
});
/** `POST /auth/v1/email/verify`: the six digits, which buy a one-time code for `/auth/v1/token`. */
export const emailVerifySchema = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
    code: z.string().regex(/^\d{6}$/),
    /** The `state` the client sent to `email/start`: ties the code to that tab's sign-in, so two tabs never collide. */
    state: z.string().max(500).optional(),
});
export const emailVerifyResponseSchema = z.object({ code: z.string(), state: z.string().optional() });
/** `POST /auth/v1/token`: OAuth 2.1 shaped, so standard clients (expo-auth-session) work. */
export const cloudTokenRequestSchema = z.discriminatedUnion("grant_type", [
    z.object({
        grant_type: z.literal("authorization_code"),
        code: z.string().min(1),
        code_verifier: z.string().min(43).max(128),
        redirect_uri: z.string().min(1),
    }),
    z.object({ grant_type: z.literal("refresh_token"), refresh_token: z.string().min(1) }),
]);
export const cloudTokenResponseSchema = z.object({
    access_token: z.string(),
    refresh_token: z.string(),
    expires_in: z.number().int(),
    token_type: z.literal("Bearer"),
});
export const organisationSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: organisationKindSchema,
    plan: cloudPlanSchema,
    role: cloudRoleSchema,
});
export const signInMethodSchema = z.object({
    id: z.string(),
    provider: signInProviderSchema,
    email: z.string().optional(),
    emailVerified: z.boolean(),
    createdAt: z.string(),
    lastUsedAt: z.string(),
});
/** `GET /cloud/v1/me`. */
export const cloudMeSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
    email: z.string().optional(),
    /** Profile picture URL from the person's sign-in provider, when it gave one. */
    picture: z.string().optional(),
    organisations: z.array(organisationSummarySchema),
    signInMethods: z.array(signInMethodSchema),
});
export const createOrganisationSchema = z.object({
    name: z.string().trim().min(1).max(100),
    /** Cloudflare Turnstile's answer, when discovery names a `turnstileSiteKey` (else ignored). */
    turnstileToken: z.string().max(2048).optional(),
});
export const renameOrganisationSchema = z.object({ name: z.string().trim().min(1).max(100) });
export const cloudMemberSchema = z.object({
    userId: z.string(),
    name: z.string().optional(),
    email: z.string().optional(),
    role: cloudRoleSchema,
    joinedAt: z.string(),
});
export const setMemberRoleSchema = z.object({ role: cloudRoleSchema });
export const createInvitationSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
    role: z.enum(["admin", "member", "viewer"]).default("member"),
});
export const cloudInvitationSchema = z.object({
    id: z.string(),
    email: z.string(),
    role: cloudRoleSchema,
    invitedBy: z.string(),
    /** The sender's name when they gave one, so the invitation still names them after they leave. */
    invitedByName: z.string().optional(),
    createdAt: z.string(),
    expiresAt: z.string(),
});
/** The answer to creating an invitation: the link is shown once, and emailed. */
export const createdInvitationSchema = cloudInvitationSchema.extend({ link: z.string() });
/** `GET /cloud/v1/invitations/:token`: what someone opening the link sees before they accept. */
export const invitationPreviewSchema = z.object({
    organisation: z.string(),
    invitedBy: z.string().optional(),
    email: z.string(),
    role: cloudRoleSchema,
    expiresAt: z.string(),
});
/**
 * `GET /platform/v1/organisation` (a device or a person, either edition): the organisation the
 * caller is in and what it is, so a workbench takes its edition from the platform, never from a
 * setting: enterprise -> Enterprise; a Cloud team -> Teams (on the teams plan); personal -> Free.
 */
export const platformOrganisationSchema = z.object({
    edition: editionSchema,
    id: z.string(),
    name: z.string(),
    kind: z.enum(["enterprise", "personal", "team"]),
    plan: cloudPlanSchema.optional(),
});
/** `POST /cloud/v1/me/sign-in-methods/link`: a five-minute ticket for `/auth/v1/start?link=` or `email/start`. */
export const linkTicketSchema = z.object({ ticket: z.string(), expiresIn: z.number().int() });
/** `DELETE /cloud/v1/me`: the person types their email address (or "delete" when they have none) to confirm. */
export const deleteAccountSchema = z.object({ confirm: z.string().trim().min(1).max(254) });
/** The most sign-ins (sessions) one person keeps at once; a new one ends the oldest. */
export const CLOUD_MAX_SESSIONS = 10;
/** One of a person's sign-ins, as `GET /cloud/v1/me/sessions` lists them. */
export const cloudSessionSchema = z.object({
    /** The sign-in's id (its token family); `DELETE /cloud/v1/me/sessions/:id` ends it. */
    id: z.string(),
    client: signInClientSchema,
    /** What signed in, read from the browser or app when it did ("Chrome on Windows", "Mojo Up app on iPhone"). */
    device: z.string().optional(),
    startedAt: z.string(),
    /** When its tokens were last issued or renewed: within the access token's life (15 minutes) of its last use. */
    lastUsedAt: z.string(),
    /** The sign-in this request came from. */
    current: z.boolean(),
});
/** `GET /cloud/v1/me/sessions`: newest use first. */
export const cloudSessionListSchema = z.object({ sessions: z.array(cloudSessionSchema), limit: z.number().int() });
//# sourceMappingURL=cloud.js.map